import 'react-loading-skeleton/dist/skeleton.css'

import trackEvents from '../../../../../constants/track-events'
import { useDiscover } from '../../../../../features/discover/hooks/useDiscover'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES
} from '../../../../../features/discover/utils'
import { trackEvent } from '../../../../../helpers/analytics'
import { Document, Page, View } from '../../compiler'
import { typoStyles } from '../../constants'

import { useTranslationContext } from '../../context/general-translations'
import { combineCulturalFitDimensions } from '../../utils/team-profile'
import CulturalFit from '../CulturalFit'
import DetailedDimensionCard from '../DetailedDimensionCard'
import DimensionCard from '../DimensionCard'
import DiscussionQuestions from '../DiscussionQuestions'
import MarkdownText from '../MarkdownText'
import TitleDescription from '../talentProfile/TitleDescription'
import TeamProfileLoading from './loading'

/**
 * Dashboard-Abweichungen gegenüber aivy-pdf-reports:
 * - requirementByDimension legt Ideal- und Übergangsbereich der gewählten
 *   Stelle unter die Teamverteilung und markiert Muss-Kriterien. Der Host
 *   rechnet beides aus career_analyse (helpers/requirement-bands). Ohne das
 *   Prop rendert das Teamprofil wie das PDF.
 * - interestsNote erklärt unter der Interessen-Überschrift, warum einzelne
 *   Interessen keine Bereiche haben. Den Text liefert der Host.
 * - useTranslationContext steht vor dem Loading-Return, damit die
 *   Hook-Reihenfolge beim Wechsel von isLoading gleich bleibt.
 * Beim Port von Upstream-Änderungen (z. B. 150746c, Kultur long mode) diese
 * Stellen erhalten.
 */
const TeamProfile = ({
  data,
  withoutAttachments,
  isLoading,
  requirementByDimension = null,
  interestsNote = null
}) => {
  const {
    enableAssistant,
    setAssistantIsTyping,
    sendMessageToAssistant,
    setAssistantFocus
  } = useDiscover()
  const { tGeneral } = useTranslationContext()

  if (isLoading) {
    return <TeamProfileLoading withoutAttachments={true} />
  }

  if (!data) return null

  const {
    spaces,
    categories,
    dimensions_with_text_modules,
    cultural_fit,
    language,
    system
  } = data

  const personalityCategory = categories[1]
  const skillsCategory = categories[2]
  const interestsCategory = categories[3]
  const cultureCategory = categories[4]

  const personalityDimensions = dimensions_with_text_modules.filter(
    ({ dimension: { category } }) => category === 1
  )

  const skillsDimensions = dimensions_with_text_modules.filter(
    ({ dimension: { category } }) => category === 2
  )

  const interestsDimensions = dimensions_with_text_modules.filter(
    ({ dimension: { category } }) => category === 3
  )

  const getBeamGraphData = ({ dimension, spaces_with_dimension_score }) => {
    const teamInputValues = spaces_with_dimension_score.map((item) => ({
      ...item,
      label: item.identification,
      value: item.score_value
    }))

    const dataset_not_complete = teamInputValues.length !== spaces.length

    return {
      teamInputValues,
      dataset_not_complete,
      ...requirementByDimension?.[dimension?.key]?.bands
    }
  }

  const isMandatory = ({ dimension }) =>
    !!requirementByDimension?.[dimension?.key]?.mandatory

  // return (
  //   <Document>
  //     <Page
  //       style={{ paddingBottom: 64, paddingTop: 64 }}
  //       size="A4"
  //       dpi={150}
  //       wrap
  //     >
  //       {/* develop */}
  //     </Page>
  //   </Document>
  // );

  const is_skills_available = !!skillsDimensions.length
  const is_personality_available = !!personalityDimensions.length
  const is_culture_available = !!cultural_fit
  const is_interests_available = !!interestsDimensions.length

  const pageProps = {
    style: { paddingTop: 64 }
    // size: getPageSize(PDF_DPI_NUMBER),
    // wrap: true
  }

  const sendDescriptionToAssistant = (description) => {
    trackEvent(trackEvents.DISCOVER_CLICK_TEAM_PROFILE_DIMENSION_TITLE)
    enableAssistant()
    setAssistantIsTyping(true)
    setTimeout(() => {
      setAssistantIsTyping(false)
      sendMessageToAssistant({
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        description
      })
      setAssistantFocus(false)
    }, 1000)
  }

  return (
    <Document>
      {/* FÄHIGKEITEN */}
      {is_skills_available && (
        <Page>
          <View>
            <TitleDescription
              title={skillsCategory.name[language]}
              description={tGeneral('skills_description')}
              isLoading={isLoading}
            />
            <View style={{ marginTop: 26.5 - 16 }} />
            {skillsDimensions.map((dimension_data, index) => (
              <View key={index} style={{ marginTop: 16 }}>
                <DimensionCard
                  language={language}
                  style={{ paddingHorizontal: 24 }}
                  dimension={dimension_data.dimension}
                  beamGraphData={getBeamGraphData(dimension_data)}
                  isMandatory={isMandatory(dimension_data)}
                  description={dimension_data.text_modules.text}
                  onClickDimensionTitle={() => {
                    sendDescriptionToAssistant(
                      dimension_data.dimension.intro[language]
                    )
                  }}
                  isLoading={isLoading}
                />
              </View>
            ))}
          </View>
        </Page>
      )}

      {/* PERSÖNLICHKEIT */}
      {is_personality_available && (
        <Page {...pageProps}>
          <View>
            <TitleDescription
              title={personalityCategory.name[language]}
              description={tGeneral('personality_description')}
              isLoading={isLoading}
            />
            <View style={{ marginTop: 26.5 - 16 }} />
            {personalityDimensions.map((dimension_data, index) => (
              <View key={index} style={{ marginTop: 16 }}>
                <DimensionCard
                  language={language}
                  style={{ paddingHorizontal: 24 }}
                  dimension={dimension_data.dimension}
                  beamGraphData={getBeamGraphData(dimension_data)}
                  isMandatory={isMandatory(dimension_data)}
                  description={dimension_data.text_modules.text}
                  onClickDimensionTitle={() => {
                    sendDescriptionToAssistant(
                      dimension_data.dimension.intro[language]
                    )
                  }}
                  isLoading={isLoading}
                />
              </View>
            ))}
          </View>
        </Page>
      )}

      {/* KULTUR */}
      {is_culture_available && (
        <Page {...pageProps}>
          <View>
            <TitleDescription
              title={cultureCategory.name[language]}
              description={tGeneral('culture_description')}
              isLoading={isLoading}
            />
            <CulturalFit
              language={language}
              style={{ marginTop: 16, paddingHorizontal: 24 }}
              culturalFitTexts={cultural_fit}
              teamInputValues={combineCulturalFitDimensions(cultural_fit)}
              challenge={system.challenges.data.HEROS_JOURNEY}
              isLoading={isLoading}
            />
          </View>
        </Page>
      )}

      {/* INTERESTS */}
      {is_interests_available && (
        <Page {...pageProps}>
          <View>
            <TitleDescription
              title={interestsCategory.name[language]}
              description={tGeneral('interests_description')}
              isLoading={isLoading}
            />
            {interestsNote && (
              <View style={{ marginHorizontal: 40, marginTop: 8 }}>
                <MarkdownText style={typoStyles.SmallBlack}>
                  {interestsNote}
                </MarkdownText>
              </View>
            )}
            <View style={{ marginTop: 26.5 - 16 }} />
            {interestsDimensions.map((dimension_data, index) => (
              <View key={index} style={{ marginTop: 16 }}>
                <DimensionCard
                  language={language}
                  style={{ paddingHorizontal: 24 }}
                  dimension={dimension_data.dimension}
                  beamGraphData={getBeamGraphData(dimension_data)}
                  isMandatory={isMandatory(dimension_data)}
                  description={dimension_data.text_modules.text}
                  onClickDimensionTitle={() => {
                    sendDescriptionToAssistant(
                      dimension_data.dimension.intro[language]
                    )
                  }}
                  isLoading={isLoading}
                />
              </View>
            ))}
          </View>
        </Page>
      )}

      {/* ___ */}
      {/* attachment */}

      {/** FÄHIGKEITEN */}
      {is_skills_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={skillsCategory.name[language]}
            description={tGeneral('skills_description')}
            isLoading={isLoading}
          />
          <View style={{ marginTop: 10 }} />
          {skillsDimensions.map((dimension_data, index) => (
            <View key={index} style={{ marginTop: 16 }}>
              <DetailedDimensionCard
                challenges={system.challenges}
                language={language}
                dimension={dimension_data.dimension}
                beamGraphData={getBeamGraphData(dimension_data)}
                textModules={dimension_data.text_modules}
                isLoading={isLoading}
              />
            </View>
          ))}
        </Page>
      )}

      {/** PERSÖNLICHKEIT */}
      {is_personality_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={personalityCategory.name[language]}
            description={tGeneral('personality_description')}
            isLoading={isLoading}
          />
          <View style={{ marginTop: 10 }} />
          {personalityDimensions.map((dimension_data, index) => (
            <View key={index} style={{ marginTop: 16 }}>
              <DetailedDimensionCard
                challenges={system.challenges}
                language={language}
                dimension={dimension_data.dimension}
                beamGraphData={getBeamGraphData(dimension_data)}
                textModules={dimension_data.text_modules}
                isLoading={isLoading}
              />
            </View>
          ))}
        </Page>
      )}

      {/* KULTUR */}
      {is_culture_available && !withoutAttachments && (
        <Page {...pageProps}>
          <View>
            <TitleDescription
              title={cultureCategory.name[language]}
              description={tGeneral('culture_description')}
              isLoading={isLoading}
            />
            <CulturalFit
              language={language}
              style={{ marginTop: 16, paddingHorizontal: 24 }}
              culturalFitTexts={cultural_fit}
              teamInputValues={combineCulturalFitDimensions(cultural_fit)}
              challenge={system.challenges.data.HEROS_JOURNEY}
              withDetails
              isLoading={isLoading}
            />
          </View>
        </Page>
      )}

      {/** BERUFLICHE INTERESSEN */}
      {is_interests_available && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={interestsCategory.name[language]}
            description={tGeneral('interests_description')}
            isLoading={isLoading}
          />
          {!isLoading && (
            <View style={{ paddingHorizontal: 40 }}>
              <DiscussionQuestions
                style={{ marginTop: 16 }}
                questions={interestsCategory.discussion_questions}
              />
            </View>
          )}
          <View style={{ marginTop: 10 }} />
          {interestsDimensions.map((dimension_data, index) => (
            <View key={index} style={{ marginTop: 16 }}>
              <DetailedDimensionCard
                challenges={system.challenges}
                language={language}
                dimension={dimension_data.dimension}
                beamGraphData={getBeamGraphData(dimension_data)}
                textModules={dimension_data.text_modules}
                isLoading={isLoading}
              />
            </View>
          ))}
        </Page>
      )}
    </Document>
  )
}

export default TeamProfile
